import React from 'react';

import { CheckMarkFillLegacy, FileError, FileSheet } from '@lyearn/icons';
import { JobEntityStatusEnum } from '@/types/schema';

import { BulkExportItemConfigType, ExportJobActions } from './types';

const IN_PROCESS_CONFIG: Omit<BulkExportItemConfigType, 'progress'> = {
  Icon: <FileSheet />,
  titleKey: 'Common:BulkExport.PreparingExport',
  descriptionKey: 'Common:BulkExport.PreparingExportDescription',
  action: { id: ExportJobActions.Cancel, textKey: 'Common:Cancel' },
};

export const EXPORT_PROGRESS_MAP = {
  [JobEntityStatusEnum.NotStarted]: 0,
  [JobEntityStatusEnum.Parsing]: 33.33,
  [JobEntityStatusEnum.Validating]: 33.33,
  [JobEntityStatusEnum.InProcess]: 66.66,
  [JobEntityStatusEnum.Completed]: 100,
  [JobEntityStatusEnum.Cancelled]: 0,
  [JobEntityStatusEnum.Failed]: 0,
  [JobEntityStatusEnum.Waiting]: 0,
};

export const exportItemConfig: Record<JobEntityStatusEnum, BulkExportItemConfigType> = {
  [JobEntityStatusEnum.NotStarted]: {
    ...IN_PROCESS_CONFIG,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.NotStarted],
  },
  [JobEntityStatusEnum.Waiting]: {
    ...IN_PROCESS_CONFIG,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Waiting],
  },
  [JobEntityStatusEnum.Parsing]: {
    ...IN_PROCESS_CONFIG,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Parsing],
  },
  [JobEntityStatusEnum.Validating]: {
    ...IN_PROCESS_CONFIG,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Validating],
  },
  [JobEntityStatusEnum.InProcess]: {
    ...IN_PROCESS_CONFIG,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.InProcess],
  },
  [JobEntityStatusEnum.Completed]: {
    Icon: <FileSheet />,
    titleKey: 'Common:BulkExport.ExportReady',
    descriptionKey: 'Common:BulkExport.ExportReadyDescription',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Completed],
    action: { id: ExportJobActions.Download, textKey: 'Common:Actions.Download' },
  },
  [JobEntityStatusEnum.Cancelled]: {
    Icon: <FileSheet className="grayscale" />,
    titleKey: 'Common:BulkExport.ExportCanceled',
    descriptionKey: 'Common:BulkExport.ExportCanceledDescription',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Cancelled],
  },
  [JobEntityStatusEnum.Failed]: {
    Icon: <FileError />,
    titleKey: 'Common:BulkExport.ExportFailed',
    descriptionKey: 'Common:BulkExport.ExportFailedDescription',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Failed],
    action: { id: ExportJobActions.TryAgain, textKey: 'Common:Actions.TryAgain' },
  },
};

const IN_PROCESS_CONFIG_BULK_UN_ASSIGN: Omit<BulkExportItemConfigType, 'progress'> = {
  Icon: <FileSheet />,
  titleKey: 'Common:BulkUnAssign.UnAssigningContent',
  descriptionKey: 'Common:BulkUnAssign.GeneratingUnassignReport',
  action: { id: ExportJobActions.Cancel, textKey: 'Common:Cancel' },
};

export const bulkUnassignItemConfig: Record<JobEntityStatusEnum, BulkExportItemConfigType> = {
  [JobEntityStatusEnum.NotStarted]: {
    ...IN_PROCESS_CONFIG_BULK_UN_ASSIGN,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.NotStarted],
  },
  [JobEntityStatusEnum.Waiting]: {
    ...IN_PROCESS_CONFIG_BULK_UN_ASSIGN,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Waiting],
  },
  [JobEntityStatusEnum.Parsing]: {
    ...IN_PROCESS_CONFIG_BULK_UN_ASSIGN,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Parsing],
  },
  [JobEntityStatusEnum.Validating]: {
    ...IN_PROCESS_CONFIG_BULK_UN_ASSIGN,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Validating],
  },
  [JobEntityStatusEnum.InProcess]: {
    ...IN_PROCESS_CONFIG_BULK_UN_ASSIGN,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.InProcess],
  },
  [JobEntityStatusEnum.Completed]: {
    Icon: <FileSheet />,
    titleKey: 'Common:BulkUnAssign.UnAssigningContent',
    descriptionKey: 'Common:BulkImport.ImportCompleteDescription',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Completed],
    rightNode: <CheckMarkFillLegacy className="h-28 w-28" fillColorToken="icon-success" />,
  },
  [JobEntityStatusEnum.Cancelled]: {
    Icon: <FileSheet className="grayscale" />,
    titleKey: 'Common:BulkUnAssign.UnAssigningContent',
    descriptionKey: 'Common:BulkUnAssign.Cancelled',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Cancelled],
  },
  [JobEntityStatusEnum.Failed]: {
    Icon: <FileError />,
    titleKey: 'Common:BulkUnAssign.UnAssigningContent',
    descriptionKey: 'Common:BulkUnAssign.Failed',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Failed],
    action: { id: ExportJobActions.Download, textKey: 'Common:BulkImport.DownloadReport' },
  },
};

const IN_PROCESS_CONFIG_BULK_FORCE_COMPLETE: Omit<BulkExportItemConfigType, 'progress'> = {
  Icon: <FileSheet />,
  titleKey: 'Common:BulkForceComplete.ForceCompletingContent',
  descriptionKey: 'Common:BulkForceComplete.ThisMayTakeAFewMinutes',
  action: undefined,
};

export const bulkForceCompleteItemConfig: Record<JobEntityStatusEnum, BulkExportItemConfigType> = {
  [JobEntityStatusEnum.NotStarted]: {
    ...IN_PROCESS_CONFIG_BULK_FORCE_COMPLETE,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.NotStarted],
  },
  [JobEntityStatusEnum.Waiting]: {
    ...IN_PROCESS_CONFIG_BULK_FORCE_COMPLETE,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Waiting],
  },
  [JobEntityStatusEnum.Parsing]: {
    ...IN_PROCESS_CONFIG_BULK_FORCE_COMPLETE,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Parsing],
  },
  [JobEntityStatusEnum.Validating]: {
    ...IN_PROCESS_CONFIG_BULK_FORCE_COMPLETE,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Validating],
  },
  [JobEntityStatusEnum.InProcess]: {
    ...IN_PROCESS_CONFIG_BULK_FORCE_COMPLETE,
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.InProcess],
  },
  [JobEntityStatusEnum.Completed]: {
    Icon: <FileSheet />,
    titleKey: 'Common:BulkForceComplete.ForceCompletingContent',
    descriptionKey: 'Common:BulkImport.ImportCompleteDescription',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Completed],
    rightNode: <CheckMarkFillLegacy className="h-28 w-28" fillColorToken="icon-success" />,
  },
  [JobEntityStatusEnum.Cancelled]: {
    Icon: <FileSheet className="grayscale" />,
    titleKey: 'Common:BulkForceComplete.ForceCompletingContent',
    descriptionKey: 'Common:BulkUnAssign.Cancelled',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Cancelled],
  },
  [JobEntityStatusEnum.Failed]: {
    Icon: <FileError />,
    titleKey: 'Common:BulkForceComplete.ForceCompletingContent',
    descriptionKey: 'Common:BulkUnAssign.Failed',
    progress: EXPORT_PROGRESS_MAP[JobEntityStatusEnum.Failed],
    action: undefined,
  },
};
